import React from 'react';
import { Box, Typography } from '@mui/material';
import { SxProps, Theme, alpha } from '@mui/material/styles';

interface BrandMarkProps {
  compact?: boolean;
  showTagline?: boolean;
  tone?: 'light' | 'dark';
  sx?: SxProps<Theme>;
}

export const BrandMark: React.FC<BrandMarkProps> = ({
  compact = false,
  showTagline = true,
  tone = 'dark',
  sx,
}) => {
  const iconSize = compact ? 36 : 42;
  const titleColor = tone === 'light' ? 'common.white' : 'text.primary';
  const accentColor = tone === 'light' ? 'info.light' : 'primary.main';
  const taglineColor = (theme: Theme) =>
    tone === 'light'
      ? alpha(theme.palette.common.white, 0.76)
      : theme.palette.text.secondary;

  return (
    <Box
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 1.25 : 1.5,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: iconSize,
          height: iconSize,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #43A1FF 0%, #2563EB 58%, #0D47A1 100%)',
          border: tone === 'light' ? '1px solid rgba(255, 255, 255, 0.14)' : '1px solid rgba(255, 255, 255, 0.76)',
          boxShadow:
            tone === 'light'
              ? '0 10px 24px rgba(4, 16, 42, 0.12)'
              : '0 12px 24px rgba(37, 99, 235, 0.18)',
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 48 48"
          role="img"
          aria-label="RentMan icon"
          sx={{
            width: compact ? 26 : 30,
            height: compact ? 26 : 30,
            overflow: 'visible',
          }}
        >
          <g transform="translate(-2.8, 0)">
            <path
              d="M11.5 12.8V29.8M11.5 12.8H21.7C25.1 12.8 27.4 15 27.4 18.1C27.4 21.1 25.1 23.3 21.7 23.3H11.5M20.5 23.3L27.4 29.8"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="3.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M29.4 29.8V14.4L36 21L42.1 14.9V29.8"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="3.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12.4 33.9C16.4 31.1 21.3 30 26.2 30.2C29.2 30.3 31.8 31.6 34.5 31.9C37.5 32.2 40 32.6 41.5 34.3"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
            <path
              d="M14 34.5C14.1 33 15.2 32.1 16.8 32.1C18.4 32.1 19.6 33 19.7 34.5M33.8 34.5C33.9 33 35 32.1 36.6 32.1C38.2 32.1 39.4 33 39.5 34.5"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="1.9"
              strokeLinecap="round"
            />
          </g>
        </Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant={compact ? 'h6' : 'h5'}
          sx={{
            color: titleColor,
            lineHeight: 1,
            fontWeight: 800,
            letterSpacing: '-0.04em',
            whiteSpace: 'nowrap',
          }}
        >
          Rent
          <Box component="span" sx={{ color: accentColor }}>
            Man
          </Box>
        </Typography>

        {showTagline ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.45,
              color: taglineColor,
              whiteSpace: 'nowrap',
            }}
          >
            Rent. Manage. Grow.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
